/**
 * Node/macOS voice I/O backends + VoiceIO composition for the CLI.
 *
 * The portable brain (state machine, barge-in, merge window, echo policy) and the WS protocol
 * clients live in src/voice/ — reusable on web (getUserMedia source + AudioContext sink + BE-minted
 * temp tokens). This file owns only what is platform-bound:
 *  - mic capture: cli/native/mic-aec.swift (Apple VPIO echo cancellation, compile-on-first-use)
 *    with raw-ffmpeg fallback (heuristic echo tier; MIC_AEC=0 forces it)
 *  - playback: per-turn ffplay fed raw PCM (persistent players drop post-gap audio)
 *
 * Design + measured numbers: mind/11-voice.md.
 */
import { spawn, spawnSync, type ChildProcess } from 'node:child_process';
import { existsSync, mkdirSync, readFileSync, statSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import { forComponent } from '../src/logging';
import { hasCommand } from '../src/platform';
import { VoiceEngine, VoiceEngineOptions } from '../src/voice/engine';
import type { Instinct } from '../src/voice/instinct';
import { CartesiaTTS } from '../src/voice/cartesia';
import { SonioxSTT } from '../src/voice/soniox';
import { type AudioSink, type AudioSource, STT_SAMPLE_RATE, TTS_SAMPLE_RATE } from '../src/voice/types';

export { CartesiaTTS, SonioxSTT, VoiceEngine };

const log = forComponent('VoiceIO');
const now = () => performance.now();

// ---------- Player: fresh ffplay per turn (a persistent one drops "late" audio after stream
// gaps — its realtime clock keeps advancing through underruns). kill() = barge-in primitive. ----------
export class Player implements AudioSink {
  private proc: ChildProcess | null = null;
  private bytesWritten = 0;
  private startedAt = 0;

  /** start a new spoken turn: kill any previous player, spawn a fresh one */
  markTurn(): void {
    this.kill();
    this.proc = spawn(
      'ffplay',
      ['-loglevel', 'quiet', '-nodisp', '-fflags', 'nobuffer', '-flags', 'low_delay', '-probesize', '32', '-f', 's16le', '-ar', String(TTS_SAMPLE_RATE), '-ch_layout', 'mono', '-i', '-'],
      { stdio: ['pipe', 'ignore', 'ignore'] },
    );
    this.proc.on('error', (e) => log.warn(`ffplay error: ${e.message}`));
    this.proc.stdin!.on('error', () => {}); // EPIPE on kill is fine
    this.bytesWritten = 0;
    this.startedAt = 0;
  }
  write(chunk: Uint8Array): void {
    if (!this.proc) this.markTurn();
    if (!this.startedAt) this.startedAt = now();
    this.bytesWritten += chunk.length;
    this.proc!.stdin!.write(chunk);
  }
  /** ms of audio actually played so far this turn */
  playedMs(): number {
    return this.startedAt ? now() - this.startedAt : 0;
  }
  /** estimated ms until queued audio finishes playing */
  drainMs(): number {
    if (!this.startedAt) return 0;
    const queuedMs = (this.bytesWritten / (TTS_SAMPLE_RATE * 2)) * 1000;
    return Math.max(0, queuedMs - (now() - this.startedAt));
  }
  kill(): void {
    this.proc?.kill('SIGKILL');
    this.proc = null;
  }
}

// ---------- mic capture: AEC helper (compile-on-first-use) → ffmpeg fallback ----------
const nativeDir = () => join(dirname(fileURLToPath(import.meta.url)), 'native');

/** avfoundation device ":0" is often a virtual loopback (BlackHole etc.) — pick a real mic by name */
function detectFfmpegMic(): string {
  if (process.env.MIC_DEVICE) return process.env.MIC_DEVICE;
  const r = spawnSync('ffmpeg', ['-f', 'avfoundation', '-list_devices', 'true', '-i', ''], { encoding: 'utf8' });
  if ((r.error as NodeJS.ErrnoException)?.code === 'ENOENT') throw new Error('ffmpeg not installed — run `brew install ffmpeg` to enable voice');
  if (r.error) throw new Error(`ffmpeg failed to run (${r.error.message}) — likely a wrong-arch binary; run \`brew reinstall ffmpeg\``);
  const out = r.stderr ?? '';
  const audio = out.slice(out.indexOf('audio devices'));
  const devices = [...audio.matchAll(/\[(\d+)\] (.+)/g)].map(([, idx, name]) => ({ idx, name: name.trim() }));
  const mic = devices.find((d) => /microphone|built-in/i.test(d.name) && !/teams|blackhole|loopback/i.test(d.name)) ?? devices[0];
  if (!mic) throw new Error('no audio input device found');
  log.debug(`ffmpeg mic: [${mic.idx}] ${mic.name}`);
  return `:${mic.idx}`;
}

/** Windows: ffmpeg's DirectShow input spec (`audio=<device name>`) for a real microphone — MIC_DEVICE
 *  (a full spec or a bare device name) wins; else the first non-virtual audio device ffmpeg lists. */
function detectDshowMic(): string {
  const env = process.env.MIC_DEVICE;
  if (env) return env.startsWith('audio=') ? env : `audio=${env}`;
  const r = spawnSync('ffmpeg', ['-hide_banner', '-list_devices', 'true', '-f', 'dshow', '-i', 'dummy'], { encoding: 'utf8', windowsHide: true });
  if ((r.error as NodeJS.ErrnoException)?.code === 'ENOENT') throw new Error('ffmpeg not installed — install it (e.g. `winget install ffmpeg`) and put it on PATH to enable voice');
  const devices = [...(r.stderr ?? '').matchAll(/"([^"]+)"\s*\(audio\)/g)].map((m) => m[1]);
  const mic = devices.find((d) => !/stereo mix|loopback|virtual|cable|teams/i.test(d)) ?? devices[0];
  if (!mic) throw new Error('no audio input device found (set MIC_DEVICE to the DirectShow device name)');
  log.debug(`ffmpeg dshow mic: ${mic}`);
  return `audio=${mic}`;
}

/** Short name of the macOS default input device (what the AEC path actually captures) + a virtual/loopback
 *  flag — so voice startup can surface "in: BlackHole 2ch ⚠" when the default isn't a real mic (silent STT). */
export function detectedInputDevice(): { name: string; virtual: boolean } | null {
  if (process.platform !== 'darwin') return null;
  let name = '';
  if (spawnSync('which', ['SwitchAudioSource']).status === 0) {
    name = (spawnSync('SwitchAudioSource', ['-c', '-t', 'input'], { encoding: 'utf8' }).stdout || '').trim();
  } else {
    const out = spawnSync('system_profiler', ['SPAudioDataType'], { encoding: 'utf8' }).stdout || '';
    // walk lines, tracking the last device-header ("        Name:" — colon, no value), and return the
    // header that owns the "Default Input Device: Yes" property line (a single regex picks the wrong block).
    let last = '';
    for (const ln of out.split('\n')) {
      const hdr = ln.match(/^\s{6,}(\S.*?):\s*$/);
      if (hdr) { last = hdr[1].trim(); continue; }
      if (/Default Input Device:\s*Yes/.test(ln)) { name = last; break; }
    }
  }
  if (!name) return null;
  return { name, virtual: /blackhole|loopback|aggregate|virtual|soundflower|multi-output|teams|zoom/i.test(name) };
}

/** Resolve (building if needed) the AEC capture binary; null → caller falls back to ffmpeg. */
export function resolveAecBinary(): string | null {
  if (process.env.MIC_AEC === '0' || process.platform !== 'darwin') return null;
  const src = join(nativeDir(), 'mic-aec.swift');
  const plist = join(nativeDir(), 'Info.plist');
  if (!existsSync(src)) return null;
  const cacheDir = join(homedir(), '.agent', 'cache');
  const bin = join(cacheDir, 'mic-aec');
  // rebuild when the source is newer than the cached binary
  if (existsSync(bin) && statSync(bin).mtimeMs >= statSync(src).mtimeMs) return bin;
  if (spawnSync('which', ['swiftc']).status !== 0) return null;
  mkdirSync(cacheDir, { recursive: true });
  log.info('compiling AEC mic helper (first run)…');
  const build = spawnSync('swiftc', ['-O', '-o', bin, src, '-Xlinker', '-sectcreate', '-Xlinker', '__TEXT', '-Xlinker', '__info_plist', '-Xlinker', plist], { encoding: 'utf8' });
  if (build.status !== 0) { log.warn(`AEC build failed: ${build.stderr?.slice(0, 400)}`); return null; }
  const sign = spawnSync('codesign', ['-fs', '-', bin], { encoding: 'utf8' });
  if (sign.status !== 0) { log.warn(`codesign failed: ${sign.stderr?.slice(0, 200)}`); return null; }
  return bin;
}

/** Jump the user straight to the macOS Microphone privacy pane (no-op off darwin). */
export function openMicSettings(): void {
  if (process.platform !== 'darwin') return;
  try { spawnSync('open', ['x-apple.systempreferences:com.apple.preference.security?Privacy_Microphone']); } catch { /* best-effort */ }
}

/** Mic TCC status via the AEC helper's non-prompting `--check-mic` probe (for /doctor). Returns null when
 *  it can't be determined (non-darwin, no helper binary, MIC_AEC=0). */
export function micPermissionStatus(): 'authorized' | 'denied' | 'notDetermined' | null {
  const bin = resolveAecBinary();
  if (!bin) return null;
  const r = spawnSync(bin, ['--check-mic'], { encoding: 'utf8' });
  const out = (r.stdout ?? '').trim();
  if (out === 'authorized') return 'authorized';
  if (out === 'denied' || out === 'restricted') return 'denied';
  if (out === 'notDetermined') return 'notDetermined';
  return null;
}

/** Why hardware echo-cancellation (AEC) isn't active — null when it is, or N/A (MIC_AEC=0, non-darwin).
 *  The actionable case is a missing Swift toolchain: the helper compiles on first use and needs `swiftc`. */
export function aecUnavailableHint(): string | null {
  if (process.env.MIC_AEC === '0' || process.platform !== 'darwin') return null;
  if (resolveAecBinary()) return null; // AEC is available
  if (spawnSync('which', ['swiftc']).status !== 0) return 'install Xcode CLI tools (`xcode-select --install`) for echo cancellation';
  return null; // build/sign failure already logged by resolveAecBinary
}

/** Node AudioSource: AEC helper when available, else raw ffmpeg (heuristic echo tier). */
export class NodeMicSource implements AudioSource {
  public readonly aec: boolean;
  private bin: string | null;
  private proc: ChildProcess | null = null;
  private stopped = false;

  constructor() {
    this.bin = resolveAecBinary();
    this.aec = !!this.bin;
  }
  start(onChunk: (pcm: Uint8Array) => void): void {
    if (this.bin) {
      this.proc = spawn(this.bin, [], { stdio: ['ignore', 'pipe', 'ignore'] });
    } else {
      if (!hasCommand('ffmpeg')) throw new Error('voice I/O unavailable: no AEC helper and no ffmpeg on PATH');
      log.info('mic: raw capture (no AEC) — echo handled heuristically; headphones recommended');
      // Capture backend: avfoundation (macOS, as before); DirectShow on Windows.
      const input = process.platform === 'win32' ? ['-f', 'dshow', '-i', detectDshowMic()] : ['-f', 'avfoundation', '-i', detectFfmpegMic()];
      this.proc = spawn(
        'ffmpeg',
        ['-loglevel', 'error', ...input, '-ar', String(STT_SAMPLE_RATE), '-ac', '1', '-f', 's16le', '-'],
        { stdio: ['ignore', 'pipe', 'pipe'], ...(process.platform === 'win32' ? { windowsHide: true } : {}) },
      );
      this.proc.stderr!.on('data', (d: Buffer) => log.warn(`ffmpeg: ${String(d).trim()}`));
    }
    this.proc.on('exit', (c) => { if (c && !this.stopped) log.error(`mic capture exited (${c}) — check mic permission / MIC_DEVICE / MIC_AEC=0`); });
    this.proc.stdout!.on('data', (chunk: Buffer) => onChunk(chunk));
  }
  stop(): void {
    this.stopped = true;
    const p = this.proc;
    this.proc = null;
    if (!p) return;
    p.kill('SIGTERM'); // graceful: mic-aec stops its audio engine (SIGKILL wedges VPIO in coreaudiod)
    setTimeout(() => { try { p.kill('SIGKILL'); } catch { /* already gone */ } }, 500).unref?.();
  }
}

// ---------- AecDuplexAudio: ONE helper process = mic source + TTS sink ----------
// The root echo fix (mind/11-voice.md): TTS rendered through the SAME AVAudioEngine as capture
// gives VPIO a sample-accurate reference for exactly what we emit — near-deterministic
// cancellation vs. the per-run spectrum of cancelling a separate ffplay via system loopback.
// Playback protocol: [u32le len][s16le 44.1k mono PCM] frames on stdin; len==0 = FLUSH (in-band,
// so stale frames already in the pipe are consumed-and-dropped — no post-barge audio race).
export class AecDuplexAudio implements AudioSource, AudioSink {
  /** Mutable: starts true (VPIO/AEC). Flips false if we fall back to non-VPIO capture (heuristic tier). */
  private _aec = true;
  get aec(): boolean { return this._aec; }
  public onFatal?: (message: string) => void;
  /** Fired once when capture degrades to the non-VPIO (no-AEC) fallback — the host switches to
   *  half-duplex so the assistant's own TTS can't bleed back as a phantom turn. */
  public onDegrade?: () => void;
  private proc: ChildProcess | null = null;
  private stopped = false;
  private micDenied = false;
  private noVpio = false;        // currently running the non-VPIO fallback
  private triedFallback = false; // one-shot guard
  private gotChunk = false;      // any mic audio since (re)spawn?
  private onChunk: (pcm: Uint8Array) => void = () => {};
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private bytesWritten = 0;
  private startedAt = 0;

  constructor(private bin: string) {}

  // --- AudioSource ---
  start(onChunk: (pcm: Uint8Array) => void): void {
    this.onChunk = onChunk;
    // Manual override (MIC_NO_VPIO=1): start directly in the non-VPIO/no-AEC mode with the same end
    // state as the auto-fallback — non-AEC + half-duplex — so the override and the fallback behave identically.
    if (process.env.MIC_NO_VPIO === '1') { this.noVpio = true; this.triedFallback = true; this._aec = false; this.onDegrade?.(); }
    this.spawnHelper();
  }

  /** (Re)spawn the helper. On the first spawn, arm a fast watchdog: if VPIO delivers NO audio within
   *  ~2.5s, the VP input tap is dead on this machine (seen on macOS 26.5.x) — respawn once with
   *  MIC_NO_VPIO=1 (plain capture, heuristic echo) so the mic actually works instead of starving STT. */
  private spawnHelper(): void {
    const env = this.noVpio ? { ...process.env, MIC_NO_VPIO: '1' } : process.env;
    this.proc = spawn(this.bin, [], { stdio: ['pipe', 'pipe', 'pipe'], env });
    this.proc.stdin!.on('error', () => {}); // EPIPE on teardown is fine
    this.proc.on('exit', (c) => { if (c && !this.stopped) log.error(`aec duplex audio exited (${c}) — check mic permission / MIC_AEC=0`); });
    this.proc.stdout!.on('data', (chunk: Buffer) => { this.gotChunk = true; this.onChunk(chunk); });
    this.proc.stderr!.on('data', (d: Buffer) => {
      for (const ln of String(d).split('\n')) {
        const s = ln.trim();
        if (!s) continue;
        // "mic access granted: false" → VPIO is muted (no capture AND no playback in this duplex engine).
        // Fatal: open the Settings pane and signal the host to tear voice down (else STT reconnect-spins
        // forever on silence). Fire once.
        if (/mic access granted:\s*false/i.test(s)) {
          if (!this.micDenied) { this.micDenied = true; openMicSettings(); this.onFatal?.('microphone permission denied — enable it in System Settings → Privacy & Security → Microphone for your terminal, then restart it'); }
        } else log.debug(`mic-aec: ${s}`);
      }
    });
    if (!this.noVpio && !this.triedFallback) {
      this.fallbackTimer = setTimeout(() => {
        if (this.stopped || this.gotChunk) return; // VPIO is delivering audio — all good
        this.triedFallback = true; this.noVpio = true; this._aec = false;
        log.warn('mic-aec: VPIO delivered no audio in 2.5s — falling back to non-VPIO capture (no AEC → half-duplex, no barge-in)');
        this.onDegrade?.(); // host → half-duplex (deaf while speaking; echo can't leak as a phantom turn)
        this.killProc();
        this.spawnHelper();
      }, 2500);
      (this.fallbackTimer as any).unref?.();
    }
  }

  /** Kill the current child WITHOUT marking the whole source stopped (used for the fallback respawn). */
  private killProc(): void {
    const p = this.proc;
    this.proc = null;
    if (!p) return;
    p.kill('SIGTERM');
    setTimeout(() => { try { p.kill('SIGKILL'); } catch { /* already gone */ } }, 500).unref?.();
  }
  stop(): void {
    this.stopped = true;
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    this.killProc();
  }

  // --- AudioSink (frame writer; same played/drain byte-math as the ffplay Player) ---
  private frame(payload: Uint8Array | null): void {
    const stdin = this.proc?.stdin;
    if (!stdin || stdin.destroyed) return;
    const hdr = Buffer.alloc(4);
    hdr.writeUInt32LE(payload ? payload.length : 0);
    stdin.write(hdr);
    if (payload?.length) stdin.write(payload);
  }
  markTurn(): void {
    this.frame(null); // FLUSH any previous turn's tail (also clears a held pause helper-side)
    this.bytesWritten = 0;
    this.startedAt = 0;
    this.pausedSince = 0;
    this.pausedAccum = 0;
  }
  write(chunk: Uint8Array): void {
    if (!this.startedAt) this.startedAt = now();
    this.bytesWritten += chunk.length;
    this.frame(chunk);
  }
  playedMs(): number {
    return this.startedAt ? now() - this.startedAt - this.pausedMs() : 0;
  }
  drainMs(): number {
    if (!this.startedAt) return 0;
    const queuedMs = (this.bytesWritten / (TTS_SAMPLE_RATE * 2)) * 1000;
    return Math.max(0, queuedMs - (now() - this.startedAt - this.pausedMs()));
  }
  /** barge-in: silence NOW (in-band flush) — the capture side keeps running */
  kill(): void {
    this.frame(null);
    this.bytesWritten = 0;
    this.startedAt = 0;
    this.pausedSince = 0;
    this.pausedAccum = 0;
  }
  /** overlap trail-off: exact-sample PAUSE (len==0xFFFFFFFF) / RESUME (len==0xFFFFFFFE) frames */
  private pausedSince = 0;
  private pausedAccum = 0;
  private ctl(code: number): void {
    const stdin = this.proc?.stdin;
    if (!stdin || stdin.destroyed) return;
    const f = Buffer.alloc(4);
    f.writeUInt32LE(code, 0);
    stdin.write(f);
  }
  pause(): void {
    if (this.pausedSince) return;
    this.pausedSince = now();
    this.ctl(0xffffffff);
  }
  resume(): void {
    if (!this.pausedSince) return;
    this.pausedAccum += now() - this.pausedSince;
    this.pausedSince = 0;
    this.ctl(0xfffffffe);
  }
  /** total paused time this turn — excluded from played/drain math (the tape held still) */
  private pausedMs(): number {
    return this.pausedAccum + (this.pausedSince ? now() - this.pausedSince : 0);
  }
  /** TEST HARNESS: queue simulated user speech (s16le 16k mono) — the helper mixes it into the real
   *  mic stream pre-gate, so the full live pipeline runs while a script plays the human. */
  inject(pcm16k: Uint8Array): void {
    const stdin = this.proc?.stdin;
    if (!stdin || stdin.destroyed) return;
    const hdr = Buffer.alloc(8);
    hdr.writeUInt32LE(0xfffffffd, 0);
    hdr.writeUInt32LE(pcm16k.length, 4);
    stdin.write(hdr);
    stdin.write(pcm16k);
  }
}

/** Spoken voice-control command, matched on the whole normalized utterance ("Voice off." → "voice off").
 *  Deterministic and pre-reflex: zero inference cost, works even if the model misbehaves. Standalone
 *  phrases only — "turn the voice off later" must NOT match. Tolerates spoken politeness ("okay…",
 *  "…please") and the merge window gluing a repeated command ("voice off voice off"). */
export const matchVoiceCommand = (text: string): 'off' | null => {
  const t = text.toLowerCase().replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim()
    .replace(/^(?:hey|ok|okay|please) /, '').replace(/ (?:please|now)$/, '');
  return /^(voice off|mute|stop listening)( \1)*$/.test(t) ? 'off' : null;
};

/** The earlier spoken turn a final REPLACES: only the one whose text the engine's `meta.supersedes` names. A final
 *  can supersede an utterance that never became a turn (absorbed after an instinct cut-in, a voice command) — then the
 *  last turn is a different one and must not be aborted or have its context reused. */
export const replacedVoiceTurn = <T extends { text: string }>(last: T | undefined, meta?: { supersedes?: string }): T | undefined =>
  meta?.supersedes !== undefined && last?.text === meta.supersedes ? last : undefined;

/** Instinct cut-in bookkeeping: once the agent cut in on a live partial, the endpointed final that barely extends it
 *  (< 3 more words) was already answered — absorbed, not re-dispatched. So is the engine's fuller RE-FINAL of that
 *  absorbed final (meta.supersedes names it): judged against the same cut. */
export class CutInAbsorber {
  private cut = '';
  private absorbed?: { text: string; cut: string };
  interjected(heard: string): void { this.cut = heard; this.absorbed = undefined; }
  absorbs(text: string, meta?: { supersedes?: string }): boolean {
    const cut = this.cut || (meta?.supersedes !== undefined && meta.supersedes === this.absorbed?.text ? this.absorbed.cut : '');
    this.cut = ''; this.absorbed = undefined;
    const words = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;
    if (!cut || words(text) - words(cut) >= 3) return false;
    this.absorbed = { text, cut };
    return true;
  }
}

/** What a barge-in cut (engine takeInterruptedReply), split into the host's two paths: the REPLY turn that was playing
 *  (→ the model's interruption note) and the worker DELIVERY it cut (→ parked; the barge-intent judge then resumes it,
 *  or has the reflex answer an aside and carry on with it, or drops it on a steer/stop). A cut delivery continues at the
 *  exact word being spoken when the engine located it from TTS word timestamps (`exact`); on the ~15 chars/s estimate
 *  it restarts at that sentence. An utterance heard to within a word is not repeated. `heard` = the delivery's part
 *  the user DID hear (the cut utterance's prefix), for the aside note. `queued` / `queuedTurn` / `exact` are optional:
 *  an older engine reports only the reply (today's behavior). */
export function splitCut(cut: { full: string; heard: string; exact?: boolean; queued?: string[]; queuedTurn?: boolean } | null):
  { reply: { full: string; heard: string } | null; delivery: string; heard: string } {
  if (!cut) return { reply: null, delivery: '', heard: '' };
  const queued = cut.queued ?? [];
  if (!cut.queuedTurn) return { reply: cut.full ? cut : null, delivery: queued.join(' ').trim(), heard: '' };
  let at = cut.full.length; // where the unheard rest starts
  if (cut.full.length - cut.heard.length > 12) {
    const sentence = Math.max(...['. ', '! ', '? '].map((b) => cut.heard.lastIndexOf(b)));
    at = cut.exact ? cut.heard.length : sentence < 0 ? 0 : sentence + 2;
  }
  const heard = cut.full.slice(0, at).trim();
  return { reply: null, delivery: [cut.full.slice(at), ...queued].map((x) => x.trim()).filter(Boolean).join(' '), heard: heard && heard + ' ' };
}

/** The host's ONE intake of a barge cut (engine takeInterruptedReply). Only the user's next VOICE line may act on it;
 *  any other turn — typed, scheduled, or a re-final replacing the cut turn — moves the conversation on, so the cut is
 *  consumed and discarded there (`forVoiceLine` false). Left unread it outlived those turns and a much-later voice
 *  "go on" replayed it. */
export function takeCut(v: { takeInterruptedReply(): Parameters<typeof splitCut>[0] } | undefined | null, forVoiceLine: boolean): ReturnType<typeof splitCut> {
  const cut = v?.takeInterruptedReply() ?? null;
  return splitCut(forVoiceLine ? cut : null);
}

/** Let the listener instinct DECIDE short overlaps (engine `overlapDecideMs`): instead of ceding on its fast ≥2-word
 *  rule — which fired ~300ms before Jev could answer, so a reaction ("Oh, that— that's new") always cut the delivery —
 *  the engine holds a short overlap, the instinct is consulted at once, and its yield / keep_talking resolves the hold.
 *  Bounded: no verdict within `decideMs` → the engine cedes. `live()` = this engine is still the session's voice. */
export function bindOverlapInstinct(v: VoiceEngine, instinct: Instinct, live: () => boolean, decideMs = 900): void {
  const o = v.options;
  const { onDiag } = o;
  o.overlapDecideMs = decideMs;
  instinct.options.overlapSettleMs = 0; // the engine now waits for the verdict — ask immediately, not after a settle
  // Consult only while the engine is still HOLDING — every hold exit ends it: cede (barge_in), resume (overlap_resume),
  // a new turn releasing the hold (tts_turn_begin), an interrupt, and the explicit overlap_end.
  o.onDiag = (ev) => {
    onDiag(ev);
    if (!live()) return;
    if (ev.kind === 'overlap_pause') void instinct.overlap(String(ev.text ?? ''));
    else if (/^(?:barge_in|overlap_resume|overlap_end|interrupt|tts_turn_begin)$/.test(ev.kind)) instinct.overlapEnded();
  };
  Object.assign(instinct.options, {
    onYield: () => {
      if (!live()) return false;
      if (v.decideOverlap('yield')) return; // the engine was holding: cede through it (a final that landed meanwhile dispatches)
      if (!v.audible) return false;
      v.interrupt(); o.onBargeIn('speaking');
    },
    // `heard` = what Jev judged: the engine absorbs only those words — a reaction that grows past them is re-decided.
    onKeepTalking: (heard: string) => (live() ? v.decideOverlap('keep_talking', heard) : false),
  });
}

/** Adaptive ack picked by the instinct (Jev: a situational ack from ACK_BANK, or silence) instead of the engine's
 *  shape-random pools ("Sure, moment." → "Okay, bye."). The engine's own verdict feeds instinct.ackFor, so the barge
 *  note claims only an ack that actually played: ack_fired → spoken; ack_skipped/ack_cancelled (silent pick, pick_late,
 *  first delta, hold, interrupt, new turn) → not. */
export function bindAckInstinct(v: VoiceEngine, instinct: Instinct): void {
  const o = v.options;
  const { onDiag } = o;
  o.pickAck = (c) => instinct.pickAck(c);
  instinct.options.ackSettleMs = 2 * o.adaptiveAckMs + 200; // past its 2nd window the engine drops a pending pick
  // Never throws: a throwing onDiag makes the engine disable the tap for good — and with it every later ack outcome.
  o.onDiag = (ev) => {
    try {
      if (ev.kind === 'ack_fired') instinct.ackOutcome(String(ev.phrase));
      else if (ev.kind === 'ack_skipped' || ev.kind === 'ack_cancelled') instinct.ackOutcome(null);
    } catch (e) { log.warn(`ack outcome tap failed: ${e instanceof Error ? e.message : e}`); }
    onDiag(ev);
  };
}

// ---------- VoiceIO: the engine + Node defaults (env keys, AEC duplex audio, ffplay fallback) ----------
export class VoiceIOOptions extends VoiceEngineOptions {
  sonioxApiKey = process.env.SONIOX_API_KEY ?? '';
  cartesiaApiKey = process.env.CARTESIA_API_KEY ?? '';
  cartesiaVoiceId = process.env.CARTESIA_VOICE_ID ?? '';
  emotions = process.env.VOICE_EMOTIONS !== '0';        // Cartesia inline emotion tags (sonic-3)
  /** CLI voice default: mask a slow reflex TTFT with a one-shot varied micro-ack (a FIXED per-turn
   *  ack was rejected as robotic — this speaks only when the reflex is actually slow). 600ms, not
   *  400: the ack is a SAFETY NET, not the voice — at 400 it fired often enough to sound synthetic
   *  (live: "started with 'hmm' too many times"); the reflex itself is the natural instant response.
   *  Engine default stays 0 (off); VOICE_ADAPTIVE_ACK_MS overrides, 0 disables. */
  adaptiveAckMs = Number(process.env.VOICE_ADAPTIVE_ACK_MS ?? 600);
  showEmotions = process.env.VOICE_SHOW_EMOTIONS === '1'; // surface the tags in the on-screen echo (debug; opt-in)
  /** Speculative reflex start (reflex starts on a stable partial, output held until the final
   *  confirms; see VoiceEngineOptions.speculativeMs). Default OFF: live A/B showed real speech
   *  fires mostly on mid-phrase pauses → correct aborts that cost latency + billing; the win only
   *  materializes when the stable partial IS the complete thought. Opt in with VOICE_SPECULATIVE=1
   *  (250ms) or VOICE_SPECULATIVE_MS=<n> while gathering real-mic data. */
  speculativeMs = process.env.VOICE_SPECULATIVE === '1' ? Number(process.env.VOICE_SPECULATIVE_MS ?? 250) : Number(process.env.VOICE_SPECULATIVE_MS ?? 0);
  /** Agent-side backchanneling ("Mm-hm." blips at clause boundaries while the user speaks a long
   *  turn; see VoiceEngineOptions.backchannelMs). Default OFF; opt in with VOICE_BACKCHANNEL=1
   *  (250ms stability window) or VOICE_BACKCHANNEL_MS=<n>. 250, not 350+: live, Soniox emits a
   *  semantic <end> within ~300-400ms of a clause pause — a longer window never fires (verified). */
  backchannelMs = process.env.VOICE_BACKCHANNEL === '1' ? Number(process.env.VOICE_BACKCHANNEL_MS ?? 250) : Number(process.env.VOICE_BACKCHANNEL_MS ?? 0);
}

export class VoiceIO extends VoiceEngine {
  private duplexSource: AecDuplexAudio | null;
  constructor(options?: Partial<VoiceIOOptions>) {
    const o = { ...new VoiceIOOptions(), ...options };
    // One duplex helper = mic + playback (root echo fix); without it: ffmpeg mic + ffplay sink.
    const bin = (!o.stt || !o.player) ? resolveAecBinary() : null;
    const duplex = bin ? new AecDuplexAudio(bin) : null;
    super({
      ...o,
      stt: o.stt ?? new SonioxSTT({ auth: o.sonioxApiKey, source: duplex ?? new NodeMicSource() }),
      tts: o.tts ?? new CartesiaTTS({ auth: o.cartesiaApiKey, voiceId: o.cartesiaVoiceId }),
      player: o.player ?? duplex ?? new Player(),
      bargeRmsMult: Number(process.env.BARGE_RMS_MULT || o.bargeRmsMult),
      bargeRmsFloor: Number(process.env.BARGE_RMS_FLOOR || o.bargeRmsFloor),
      overlapEnergyHold: process.env.OVERLAP_ENERGY_HOLD === '1' || o.overlapEnergyHold, // textless residue pre-pause: opt-in (hiccup source)
    });
    this.duplexSource = duplex;
    // Non-VPIO fallback has no echo cancellation → go half-duplex so the assistant's TTS can't bleed
    // back as a phantom turn (the cleaner fix than a leaky heuristic gate; cost: no barge-in).
    if (duplex) duplex.onDegrade = () => this.setBargeIn(false);
    // Route provider diagnostics (STT/TTS errors, breaker, reconnects) through the engine's onDiag
    // tap so one sink sees the whole session. Test fakes have no onDiag — guard on the real classes.
    if (this.stt instanceof SonioxSTT) this.stt.onDiag = ({ t: _t, kind, ...rest }) => this.diag(kind, rest);
    if (this.tts instanceof CartesiaTTS) this.tts.onDiag = ({ t: _t, kind, ...rest }) => this.diag(kind, rest);
  }

  /** Host hook for an unrecoverable audio failure — mic permission denied (duplex source) or no mic
   *  audio at all (STT watchdog). Routed to whichever can detect it. */
  set onFatal(fn: (message: string) => void) {
    if (this.duplexSource) this.duplexSource.onFatal = fn;
    if (this.stt) this.stt.onFatal = fn;
  }

  /** ready = keys present (AEC vs heuristic is decided at start()) */
  static available(env = process.env): boolean {
    return !!(env.SONIOX_API_KEY && env.CARTESIA_API_KEY && env.CARTESIA_VOICE_ID);
  }
}

// ---------- test seam: file-driven fake STT + silent TTS/player ----------
//  The voice RENDER path lives in the cli REPL (echo/suspend/redraw closures), reachable only by
//  running the real binary — a mic makes that un-CI-able. With BOTH stt and player supplied, the
//  VoiceIO ctor skips the AEC helper entirely (no audio hardware touched). The fake STT polls a text
//  file and fires `onUtterance` per appended line, so an expect/PTY harness drives real turns by
//  appending utterances. Gated by AGENTX_VOICE_FAKE=<file> in cli.ts startVoice. NOT a product path.
export function fakeVoiceParts(uttFile: string): { stt: AudioSource & any; tts: any; player: AudioSink } {
  let timer: ReturnType<typeof setInterval> | null = null;
  let offset = 0;
  const stt: any = {
    usingAec: true,
    onPartial: (_: string) => {},
    onUtterance: (_: string, __: number) => {},
    onLevel: (_: number) => {},
    start() {
      timer = setInterval(() => {
        let buf: string;
        try { buf = readFileSync(uttFile, 'utf8'); } catch { return; }
        if (buf.length <= offset) return;
        const fresh = buf.slice(offset); offset = buf.length;
        for (const line of fresh.split('\n')) { const t = line.trim(); if (t) stt.onUtterance(t, Date.now()); }
      }, 100); // NOT unref'd: an unref'd interval can be starved when only the TTY keeps the loop alive; stop() clears it
    },
    reset() {},
    stop() { if (timer) { clearInterval(timer); timer = null; } },
  };
  const tts: any = { connect() {}, newContext() {}, speak() {}, end() { tts.onDone?.(); }, cancel() {}, close() {}, onAudio: (_: Uint8Array) => {}, onDone: () => {} };
  // No pause/resume: that would make the engine `overlapCapable`, and an injected utterance arriving
  // while a prior turn is still "speaking" would be silently dropped as overlap/backchannel — the test
  // needs every injected line to dispatch deterministically.
  const player: AudioSink = { markTurn() {}, write() {}, drainMs: () => 0, playedMs: () => 0, kill() {} };
  return { stt, tts, player };
}
